import React, { useState } from 'react';
import {
    Box,
    Card,
    Chip,
    IconButton,
    Typography,
    Stack,
    CircularProgress,
    Button,
    Grid,
    Divider,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Avatar
} from '@mui/material';
import { ArrowBack, CheckCircle, Storefront, Email } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useComplaint, useResolveComplaint, useNotifyVendorComplaint } from 'hooks/useComplaints';
import { format } from 'date-fns';

const ComplaintDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { data: complaint, isLoading, error } = useComplaint(id);
    const resolveMutation = useResolveComplaint();
    const notifyMutation = useNotifyVendorComplaint();

    const [openDialog, setOpenDialog] = useState(false);
    const [notifyMessage, setNotifyMessage] = useState('');
    const [notifySuccess, setNotifySuccess] = useState('');

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !complaint) {
        return (
            <Box p={4}>
                <Alert severity="error">Failed to load complaint details. It may have been deleted.</Alert>
                <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/complaints')} sx={{ mt: 2 }}>
                    Back to List
                </Button>
            </Box>
        );
    }

    const handleResolve = () => {
        const newStatus = complaint.status === 'resolved' ? 'pending' : 'resolved';
        resolveMutation.mutate({ id, status: newStatus });
    };

    const handleNotifyVendor = () => {
        if (!notifyMessage.trim()) return;
        notifyMutation.mutate(
            { id, message: notifyMessage },
            {
                onSuccess: () => {
                    setNotifySuccess('Notification sent to vendor successfully');
                    setOpenDialog(false);
                    setNotifyMessage('');
                    setTimeout(() => setNotifySuccess(''), 3000);
                }
            }
        );
    };

    const reporterName = complaint.user ? `${complaint.user.firstName} ${complaint.user.lastName}` : 'Anonymous';

    return (
        <Box p={{ xs: 2, md: 4 }}>
            <Stack direction="row" alignItems="center" spacing={2} mb={4}>
                <IconButton onClick={() => navigate('/admin/complaints')} sx={{ bgcolor: 'white', boxShadow: 1 }}>
                    <ArrowBack />
                </IconButton>
                <Box flex={1}>
                    <Typography variant="h4" fontWeight={800} color="primary.main">
                        Ticket Detail {complaint.ticketId ? `#${complaint.ticketId}` : ''}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                    <Button
                        variant={complaint.status === 'resolved' ? 'outlined' : 'contained'}
                        color={complaint.status === 'resolved' ? 'warning' : 'success'}
                        startIcon={<CheckCircle />}
                        onClick={handleResolve}
                        disabled={resolveMutation.isPending}
                    >
                        {resolveMutation.isPending ? 'Updating...' : complaint.status === 'resolved' ? 'Reopen Ticket' : 'Mark Resolved'}
                    </Button>
                </Stack>
            </Stack>

            {notifySuccess && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    {notifySuccess}
                </Alert>
            )}

            <Grid container spacing={4}>
                {/* Left Side: Ticket Content */}
                <Grid item xs={12} md={8}>
                    <Card sx={{ p: 4, borderRadius: 3, mb: 4 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                            <Typography variant="h5" fontWeight="bold">
                                {complaint.title}
                            </Typography>
                            <Chip
                                label={complaint.status?.toUpperCase() || 'UNKNOWN'}
                                color={complaint.status === 'resolved' ? 'success' : complaint.status === 'reviewed' ? 'info' : 'warning'}
                            />
                        </Stack>

                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, mb: 4 }}>
                            {complaint.description}
                        </Typography>

                        {/* Evidence/Attachments - Assuming complaint.attachments array exists or similar */}
                        {complaint.attachments && complaint.attachments.length > 0 && (
                            <Box mt={4}>
                                <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                    Attachments
                                </Typography>
                                <Stack direction="row" spacing={2} flexWrap="wrap">
                                    {complaint.attachments.map((img, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{ width: 120, height: 120, borderRadius: 2, overflow: 'hidden', border: '1px solid #ddd' }}
                                        >
                                            <img
                                                src={img}
                                                alt={`Attachment ${idx}`}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Card>
                </Grid>

                {/* Right Side: Metadata and Actions */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                            Reporter Information
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <Avatar>{reporterName.charAt(0)}</Avatar>
                            <Box>
                                <Typography variant="body1" fontWeight="bold">
                                    {reporterName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {complaint.userType === 'customer' ? 'Customer Account' : 'Vendor/Staff Account'}
                                </Typography>
                            </Box>
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        <Box mb={2}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Received
                            </Typography>
                            <Typography variant="body2">{format(new Date(complaint.createdAt), 'PPpp')}</Typography>
                        </Box>
                        <Box mb={2}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Order Reference
                            </Typography>
                            <Typography variant="body2">{complaint.orderId ? `#${complaint.orderId}` : 'N/A'}</Typography>
                        </Box>
                    </Card>

                    <Card sx={{ p: 3, borderRadius: 3 }}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                            Vendor Coordination
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={3}>
                            If this complaint involves a vendor, you can send them a direct platform notification to request a resolution or
                            response.
                        </Typography>
                        <Button variant="outlined" fullWidth startIcon={<Storefront />} onClick={() => setOpenDialog(true)}>
                            Notify Vendor
                        </Button>
                    </Card>
                </Grid>
            </Grid>

            {/* Notify Vendor Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Send Warning/Notification to Vendor</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        This message will be sent to the vendor associated with this complaint (or order) and appear in their dashboard
                        notifications.
                    </Typography>
                    <TextField
                        margin="dense"
                        label="Message content"
                        type="text"
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        value={notifyMessage}
                        onChange={(e) => setNotifyMessage(e.target.value)}
                        placeholder="e.g. Please review this customer complaint regarding missing items..."
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenDialog(false)} disabled={notifyMutation.isPending}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleNotifyVendor}
                        variant="contained"
                        color="primary"
                        startIcon={<Email />}
                        disabled={!notifyMessage.trim() || notifyMutation.isPending}
                    >
                        {notifyMutation.isPending ? 'Sending...' : 'Send Notification'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ComplaintDetail;
